import {Injectable} from '@angular/core';
import * as signalR from '@microsoft/signalr';
import {Subject} from 'rxjs';
import {JobFinishedNotificationInfos} from '../Model/signalr/JobFinishedNotificationInfos';
import {SubscriptionFeed} from '../Model/signalr/SubscriptionFeed';

@Injectable({
  providedIn: 'root'
})
export class SignalrService {
  hubConnection: signalR.HubConnection;
  shouldDisconnect: boolean = false;
  currentRunningFeeds: SubscriptionFeed[] = [];
  private reconnectTimeout: any = null;
  private currentOpenedCatalogId: string = null;
  private clearSubscriptionsSubject = new Subject<void>();
  clearSubscriptions$ = this.clearSubscriptionsSubject.asObservable();
  private refreshingCatalogSubject = new Subject<void>();
  refreshCatalog$ = this.refreshingCatalogSubject.asObservable();
  private refreshCatalogListSubject = new Subject<void>();
  refreshCatalogList$ = this.refreshCatalogListSubject.asObservable();
  private showJobRunningWarningSubject = new Subject<boolean>();
  showJobRunningWarning$ = this.showJobRunningWarningSubject.asObservable();
  private showJobFinishedNotificationSubject = new Subject<JobFinishedNotificationInfos>();
  ShowJobFinishedNotification$ = this.showJobFinishedNotificationSubject.asObservable();
  private finishedJobSubject = new Subject<{ jobId: string }>();
  finishedJob$ = this.finishedJobSubject.asObservable();
  private jobUpdateSubject = new Subject<{ CustomerId: string }>();
  jobUpdateSubject$ = this.jobUpdateSubject.asObservable();
  private jobCreatedUpdateSubject = new Subject<{ CustomerId: string }>();
  jobCreatedUpdateSubject$ = this.jobCreatedUpdateSubject.asObservable();
  private jobDetailUpdateSubject = new Subject<{ jobId: string }>();
  jobDetailUpdateSubject$ = this.jobDetailUpdateSubject.asObservable();

  constructor(private builder: signalR.HubConnectionBuilder) {
  }

  async startConnection(urlPath: string, accessToken: string) {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.hubConnection) {
      this.shouldDisconnect = true;
      try {
        await this.hubConnection.stop();
      } catch { }
    }
    this.shouldDisconnect = false;

    const url = `${location.origin}/${urlPath}`;
    this.hubConnection = this.builder
      .withUrl(url, {
        accessTokenFactory: () => accessToken
      }).build();

    this.hubConnection.onclose(() => {
      if (!this.shouldDisconnect) {
        console.log('Connection lost. Attempting to reconnect...');
        this.reconnectTimeout = setTimeout(() => this.startConnection(urlPath, accessToken), 3000);
      } else {
        this.shouldDisconnect = false;
      }
    });

    this.hubConnection.on('RefreshCatalog', () => {
      this.refreshingCatalogSubject.next();
    });
    this.hubConnection.on('RefreshCatalogList', () => {
      this.refreshCatalogListSubject.next();
    });
    this.hubConnection.on('ShowJobRunningWarning', (showWarning: boolean) => {
      this.showJobRunningWarningSubject.next(showWarning);
    });
    this.hubConnection.on('ShowJobFinishedNotification', (jobName: string, jobState: string, jobType: string) => {
      this.showJobFinishedNotificationSubject.next({jobName, jobState, jobType});
    });
    this.hubConnection.on('FinishedJob', (jobId: string) => {
      this.finishedJobSubject.next({jobId});
    });
    this.hubConnection.on('JobUpdates', (CustomerId: string) => {
      this.jobUpdateSubject.next({CustomerId});
    });
    this.hubConnection.on('JobCreatedUpdates', (CustomerId: string) => {
      this.jobCreatedUpdateSubject.next({CustomerId});
    });
    this.hubConnection.on('JobDetailUpdates', (jobId: string) => {
      this.jobDetailUpdateSubject.next({jobId});
    });
    try {
      await this.hubConnection.start();
      console.log('Connection started');
    } catch (err) {
      console.log('Error while starting connection: ' + err);
    }

    this.currentRunningFeeds.forEach(async subscriptionFeed => {
      await this.send(subscriptionFeed.method, ...subscriptionFeed.args);
    });
  }

  async stopConnection(clearSubscriptions: boolean) {
    this.shouldDisconnect = true;
    if (clearSubscriptions) {
      this.clearSubscriptionsSubject.next();
    }
    await this.hubConnection.stop();
  }

  async send(method: string, ...args: unknown[]): Promise<any> {
    if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
      try {
        return await this.hubConnection.invoke(method, ...args);
      } catch (error) {
        if (error.message != "Invocation canceled due to the underlying connection being closed.") {
          console.error('Error invoking SignalR method:', error);
          throw error;
        }
      }
    } else {
      console.warn('Connection is not in Connected state. Attempting to reconnect...');
      try {
        await this.hubConnection.start();
        return await this.hubConnection.invoke(method, ...args);
      } catch (error) {
        console.error('Failed to reconnect and send message:', error);
        throw error;
      }
    }
  }

  keyOf = (m: string, id: string) => `${m}::${id}`;

  async startFinishedJobFeed(jobId: string) {
    const method = 'StartJob';
    const key = this.keyOf(method, jobId);
    const exists = this.currentRunningFeeds.some(
      x => this.keyOf(x.method, String(x.args[0])) === key
    );
    if (exists) return;
    const sub = new SubscriptionFeed(method, [jobId]);
    await this.send(sub.method, ...sub.args);
    this.currentRunningFeeds.push(sub);
  }


  async stopFinishedJobFeed(jobId: string) {
    const joinKey = this.keyOf('StartJob', jobId);
    const i = this.currentRunningFeeds.findIndex(x => this.keyOf(x.method, String(x.args[0])) === joinKey);
    if (i > -1) this.currentRunningFeeds.splice(i, 1);
    await this.send('EndJob', jobId);
  }

  async startJobUpdateFeed(customerID: string) {
    const method = 'SubscribeJobUpdates';
    const key = this.keyOf(method, customerID);
    const exists = this.currentRunningFeeds.some(
      x => this.keyOf(x.method, String(x.args[0])) === key
    );
    if (exists) return;
    const sub = new SubscriptionFeed(method, [customerID]);
    await this.send(sub.method, ...sub.args);
    this.currentRunningFeeds.push(sub);
  }

  async removeJobUpdate(customerID: string) {
    const method = 'SubscribeJobUpdates';
    const key = this.keyOf(method, customerID);
    const index = this.currentRunningFeeds.findIndex(x => this.keyOf(x.method, String(x.args[0])) === key);
    if (index > -1) {
      this.currentRunningFeeds.splice(index, 1);
      await this.send('UnsubscribeJobUpdates', customerID);
    }
  }

  async startJobDetailUpdateFeed(jobId: string) {
    const method = 'SubscribeJobDetailUpdates';
    const key = this.keyOf(method, jobId);
    const exists = this.currentRunningFeeds.some(
      x => this.keyOf(x.method, String(x.args[0])) === key
    );
    if (exists) return;
    const sub = new SubscriptionFeed(method, [jobId]);
    await this.send(sub.method, ...sub.args);
    this.currentRunningFeeds.push(sub);
  }

  async removeJobDetailUpdate(jobId: string) {
    const method = 'SubscribeJobDetailUpdates';
    const key = this.keyOf(method, jobId);
    const index = this.currentRunningFeeds.findIndex(x => this.keyOf(x.method, String(x.args[0])) === key);
    if (index > -1) {
      this.currentRunningFeeds.splice(index, 1);
      await this.send('UnsubscribeJobDetailUpdates', jobId);
    }
  }

  async startCatalogFeed(customerId: string, catalogId: string) {
    var currentCatalogFeedIndex = this.currentRunningFeeds.findIndex(x => x.method == 'OpenCatalog');
    if (currentCatalogFeedIndex > -1)
      return;
    var subscriptionFeed = new SubscriptionFeed('OpenCatalog', [customerId, catalogId]);
    await this.send(subscriptionFeed.method, ...subscriptionFeed.args);
    this.currentRunningFeeds.push(subscriptionFeed);
    this.currentOpenedCatalogId = catalogId;
  }

  async stopCatalogFeed(customerId: string, catalogId: string) {
    var currentCatalogFeedIndex = this.currentRunningFeeds.findIndex(x => x.method == 'OpenCatalog');
    var subscriptionFeedCloser = new SubscriptionFeed('CloseCatalog', [customerId, this.currentOpenedCatalogId])

    if (this.currentOpenedCatalogId && this.currentOpenedCatalogId !== catalogId) {
      if (currentCatalogFeedIndex > -1) {
        this.currentRunningFeeds.splice(currentCatalogFeedIndex, 1);
      }
      await this.send(subscriptionFeedCloser.method, ...subscriptionFeedCloser.args);
      this.currentOpenedCatalogId = null;
    }
  }

  async startCustomerFeed(customerId: string) {
    var currentCatalogFeedIndex = this.currentRunningFeeds.findIndex(x => x.method == 'OpenCatalogList');
    if (currentCatalogFeedIndex > -1)
      return;

    var subscriptionFeed = new SubscriptionFeed('OpenCatalogList', [customerId]);
    await this.send(subscriptionFeed.method, ...subscriptionFeed.args);
    this.currentRunningFeeds.push(subscriptionFeed);
  }

  async stopCustomerFeed(customerId: string) {
    var currentCatalogFeedIndex = this.currentRunningFeeds.findIndex(x => x.method == 'OpenCatalogList');
    var subscriptionFeedCloser = new SubscriptionFeed('CloseCatalogList', [customerId])
    if (currentCatalogFeedIndex > -1) {
      this.currentRunningFeeds.splice(currentCatalogFeedIndex, 1);
    }
    await this.send(subscriptionFeedCloser.method, ...subscriptionFeedCloser.args);
  }
}
